package com.hyperether.goodjob.scenes.contacts

import androidx.compose.runtime.Composable
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.lifecycle.viewModelScope
import com.hyperether.goodjob.getPlatformChecker
import com.hyperether.goodjob.map.Marker
import com.hyperether.goodjob.models.Contact
import com.hyperether.goodjob.models.ContactType
import com.hyperether.goodjob.models.Job
import com.hyperether.goodjob.models.Site
import com.hyperether.goodjob.repository.IndustryManager
import com.hyperether.goodjob.repository.Repository
import com.hyperether.goodjob.repository.WebRepositoryImpl
import com.hyperether.goodjob.repository.remote.model.LatLng
import com.hyperether.goodjob.repository.remote.model.ParsedAddress
import com.hyperether.goodjob.repository.remote.model.Resource
import com.hyperether.goodjob.resources.Res
import com.hyperether.goodjob.resources.contact_deleted
import com.hyperether.goodjob.resources.contact_type_contractor
import com.hyperether.goodjob.resources.contact_type_customer
import com.hyperether.goodjob.resources.contact_type_supplier
import com.hyperether.goodjob.resources.site_added
import com.hyperether.goodjob.resources.unexpected_error
import com.hyperether.goodjob.resources.unknown_error
import com.hyperether.goodjob.scenes.JobsViewModel
import com.hyperether.resources.stringResource
import com.hyperether.resources.stringResourcePlain
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.StringResource
import org.lighthousegames.logging.logging

class ContactsViewModel(repository: Repository) : JobsViewModel(repository) {

    val log = logging("ContactsViewModel")

    val showEditPopUp: MutableState<Boolean> = mutableStateOf(false)
    val isInEditMode: MutableState<Boolean> = mutableStateOf(false)

    val showPopUpSuccess: MutableState<Boolean> = mutableStateOf(false)
    val showPopUpError: MutableState<Boolean> = mutableStateOf(false)
    val showPopUpSuccessDeleted: MutableState<Boolean> = mutableStateOf(false)
    val showLoader: MutableState<Boolean> = mutableStateOf(false)
    val errorText: MutableState<String> = mutableStateOf("")
    val successApiText: MutableState<String> = mutableStateOf("")
    val siteNameInput: MutableState<String> = mutableStateOf("")
    val contactNameInput: MutableState<String> = mutableStateOf("")
    val addressSiteInput: MutableState<String> = mutableStateOf("")
    val phoneSiteInput: MutableState<String> = mutableStateOf("")
    val siteEmailInput: MutableState<String> = mutableStateOf("")
    val siteContactNameInput: MutableState<String> = mutableStateOf("")
    val siteAddressInput: MutableState<String> = mutableStateOf("")
    var place: MutableState<ParsedAddress?> = mutableStateOf(null)
    val sitePhoneInput: MutableState<String> = mutableStateOf("")

    val currentSitesForContact = mutableStateListOf<Site>()

    private val _contacts = MutableStateFlow<List<Contact>>(emptyList())
    val contacts: StateFlow<List<Contact>> = _contacts

    //web states
    val contactList = mutableStateListOf<Contact>()
    private val _site = MutableStateFlow<Site?>(null)
    val site: StateFlow<Site?> get() = _site
    private val _contact = MutableStateFlow<Contact?>(null)
    val contact: StateFlow<Contact?> get() = _contact

    val selectedContacts = mutableStateListOf<Contact>()
    var selectedCities = mutableStateOf(setOf<String>())
    var selectedIndustries = mutableStateOf(setOf<String>())
    var selectedContactTypes = mutableStateOf(setOf<String>())

    private val _jobs = MutableStateFlow<List<Job>>(emptyList())
    val jobs: StateFlow<List<Job>> = _jobs

    var places = mutableStateListOf<ParsedAddress>()
    var contactTypeMap: Map<String, String> = mutableMapOf()

    val contactTypes: Map<String, StringResource> = mapOf(
        ContactType.customer.name to Res.string.contact_type_customer,
        ContactType.supplier.name to Res.string.contact_type_supplier,
        ContactType.contractor.name to Res.string.contact_type_contractor
    )

    init {
        tag = "ContactsViewModel"
        if (getPlatformChecker().isMobile()) {
            init()
            loadRemoteData()
        }
    }

    fun init() {
        resetFilter()
        clearFilterValues()
        // initViewModel will fetch data on web: team, sites, jobs
        initViewModel()

        log.d("getContactsFlow ContactsViewModel") { "init isMobile: ${getPlatformChecker().isMobile()}" }
        viewModelScope.launch {
            repository.getContactsFlow().collect { newContacts ->
                _contacts.value = newContacts
                log.d("ContactsViewModel") { "init contacts: ${contacts.value.size}" }
                filterContacts()
            }
        }
    }

    override fun loadRemoteData() {
        super.loadRemoteData()

        repository.getWorkspaceId().isNotEmpty().let {
            viewModelScope.launch {
                val result = repository.getAllContactsByWorkspace()
                if (result is Resource.Success) {
                    println("Fetched sites: ${result.data}")
                    _contacts.value = result.data ?: emptyList()
                    log.d("ContactsViewModel") { "loadRemoteData contacts: ${contacts.value.size}" }
                    filterContacts()
                } else {
                    println("Error fetching sites: ${result.text}")
                }
            }
        }
    }

    private suspend fun getSiteById(id: String): Site? {
        return repository.getSiteById(id)
    }

    fun fetchSiteById(id: String) {
        viewModelScope.launch {
            _site.value = getSiteById(id)
        }
    }

    fun fetchContactById(id: String) {
        viewModelScope.launch {
            _contact.value = getContactById(id)
        }
    }

    fun fetchJobsByParentId(parentId: String) {
        viewModelScope.launch {
            repository.getJobsByContactIdFlow(parentId).collect {
                _jobs.value = it
            }
        }
    }

    fun addSite(site: Site) {
        viewModelScope.launch {
            when (val result = repository.addSite(site)) {
                is Resource.Success -> {
                    showLoader.value = false
                    showPopUpSuccess.value = true
                    successApiText.value = stringResourcePlain(Res.string.site_added)
                    println("Site added successfully")
                }

                is Resource.Error -> {
                    showLoader.value = false
                    showPopUpError.value = true
                    errorText.value = result.text ?: stringResourcePlain(Res.string.unknown_error)
                    println("Error while adding site")
                }
            }
        }
    }

    fun updateSite(siteId: String, site: Site, onSuccess: () -> Unit, onError: (String) -> Unit) {
        viewModelScope.launch {
            when (val result = repository.updateSite(siteId, site)) {
                is Resource.Success -> {
                    onSuccess()
                }

                is Resource.Error -> {
                    onError(result.text ?: stringResourcePlain(Res.string.unknown_error))
                    errorText.value = result.text ?: stringResourcePlain(Res.string.unknown_error)
                }
            }
        }
    }

    fun clearSiteValues() {
        siteNameInput.value = ""
        siteEmailInput.value = ""
        siteContactNameInput.value = ""
        siteAddressInput.value = ""
        sitePhoneInput.value = ""
        places.clear()
        place.value = null
    }

    fun getSitesForContact(contactId: String) {
        viewModelScope.launch {
            val result = repository.getSitesByContactId(contactId)
            currentSitesForContact.clear()
            currentSitesForContact.addAll(result)
        }
    }

    fun deleteSelectedContacts() {
        if (selectedContacts.isNotEmpty()) {
            showLoader.value = true
            viewModelScope.launch {
                try {
                    selectedContacts.forEachIndexed { index, contact ->
                        val isLastItem = index == selectedContacts.size - 1
                        contact.id?.let { id ->
                            val result = repository.deleteContactById(id)
                            if (result is Resource.Success) {
                                if (repository is WebRepositoryImpl) {
                                    loadRemoteData()
                                }
                                contactList.remove(contact)
                                if (isLastItem) {
                                    isInEditMode.value = false
                                    showPopUpSuccess.value = true
                                    successApiText.value =
                                        stringResourcePlain(Res.string.contact_deleted)
                                    showEditPopUp.value = false
                                    showLoader.value = false
                                    selectedContacts.clear()
                                }
                            } else if (result is Resource.Error) {
                                errorText.value =
                                    result.text ?: stringResourcePlain(Res.string.unknown_error)
                                isInEditMode.value = false
                                showPopUpError.value = true
                                showLoader.value = false
                                showEditPopUp.value = false
                                return@launch
                            }
                        }
                    }
                } catch (e: Exception) {
                    isInEditMode.value = false
                    showPopUpError.value = true
                    showLoader.value = false
                    showEditPopUp.value = false
                }
            }
        }
    }

    fun deleteSite(
        id: String,
        onSuccess: () -> Unit,
        onError: (String) -> Unit
    ) {
        viewModelScope.launch {
            try {
                val result = repository.deleteSiteById(id)
                if (result is Resource.Success) {
                    _sites.value = _sites.value.filter { it.id != id }
                    onSuccess()
                } else if (result is Resource.Error) {
                    errorText.value = result.text ?: stringResourcePlain(Res.string.unknown_error)
                    onError(errorText.value)
                    return@launch
                }
            } catch (e: Exception) {
                onError(stringResourcePlain(Res.string.unexpected_error) + " ${e.message}")
            }
        }
    }

    suspend fun getContactById(id: String): Contact? {
        return repository.getContact(id)
    }

    fun filterContacts() {
        contactList.clear()
        val lists =
            listOf(selectedCities.value, selectedIndustries.value, selectedContactTypes.value)
        if (selectedCities.value.isEmpty() &&
            selectedIndustries.value.isEmpty() &&
            selectedContactTypes.value.isEmpty()
        ) {
            contactList.addAll(_contacts.value)
        } else {
            log.d("ContactsViewModel") { "Filtering contacts with lists: $lists" }
            contactList.addAll(_contacts.value.filter { contact ->
                selectedCities.value.any { query ->
                    contact.addressObject?.city?.equals(query, ignoreCase = true) == true
                }.or(selectedCities.value.isEmpty()) && selectedIndustries.value.any { query ->
                    contact.industry?.equals(query, ignoreCase = true) == true
                }
                    .or(selectedIndustries.value.isEmpty()) && selectedContactTypes.value.any { query ->
                    contact.contactType?.equals(query, ignoreCase = true) == true
                }.or(selectedContactTypes.value.isEmpty())
            })
        }
        log.d("ContactsViewModel") { "contactList: ${contactList.size}" }
    }

    fun clearFilterValues() {
        selectedCities.value = emptySet()
        selectedIndustries.value = emptySet()
        selectedContactTypes.value = emptySet()
    }

    fun getSitesForContactRemote(contactId: String) {
        viewModelScope.launch {
            val result = repository.getSitesForContactRemote(contactId)
            if (result is Resource.Success) {
                println("Fetched sites: ${result.data}")
                currentSitesForContact.clear()
                currentSitesForContact.addAll(result.data ?: emptyList())
            } else {
                println("Error fetching sites: ${result.text}")
            }
        }
    }

    fun findPlace(name: String) {
        viewModelScope.launch {
            val list = repository.findPlace(name)
            places.clear()
            places.addAll(list?.toList() ?: emptyList())
        }
    }

    private fun getCoordinates(): Pair<Double, Double>? {
        val lat = site.value?.addressObject?.lat
            ?: contact.value?.addressObject?.lat
        val long = site.value?.addressObject?.long
            ?: contact.value?.addressObject?.long

        return if (lat != null && long != null) lat to long else null
    }

    private fun getRemoteSiteCoordinates(): Pair<Double, Double>? {
        val lat = site.value?.addressObject?.lat
        val long = site.value?.addressObject?.long

        return if (lat != null && long != null) lat to long else null
    }

    fun getSiteOrContactMarkers(): List<Marker> {
        return getCoordinates()?.let { (lat, long) ->
            listOf(Marker(lat = lat, lng = long, title = contact.value?.name.orEmpty()))
        } ?: emptyList()
    }

    fun getRemoteSiteMarkers(): List<Marker> {
        return getRemoteSiteCoordinates()?.let { (lat, long) ->
            listOf(Marker(lat = lat, lng = long, title = ""))
        } ?: emptyList()
    }

    fun getSiteOrContactCurrentPosition(): LatLng? {
        return getCoordinates()?.let { (lat, long) ->
            LatLng(lat, long)
        }
    }

    @Composable
    fun refreshFilter(contacts: List<Contact>) {
        log.d("ContactsViewModel") { "refreshFilter contacts: ${contacts.size}" }
        val cities = contacts.mapNotNull { it.addressObject?.city }.distinct()
        val industriesInContacts = contacts.mapNotNull { it.industry?.trim() }.toSet()
        val industries =
            IndustryManager.industries.value.filter { it.trim() in industriesInContacts }
        val contactTypes = contacts.mapNotNull { it.contactType }.map { contactType ->
            val type =
                contactTypes.entries.find { item -> item.key == contactType }?.value
            if (type != null) {
                stringResource(type)
            } else {
                contactType
            }
        }.distinct()

        // Ensure selectedContactTypes only contains valid contactTypes (Set version)
        val originalSelectedContactTypes = selectedContactTypes.value
        val filteredSelectedContactTypes =
            originalSelectedContactTypes.filter { it in contactTypes }.toSet()
        val removedContactTypes = originalSelectedContactTypes - filteredSelectedContactTypes
        selectedContactTypes.value = filteredSelectedContactTypes

        // Ensure selectedIndustries only contains valid industries (Set version)
        val originalSelectedIndustries = selectedIndustries.value
        val filteredSelectedIndustries =
            originalSelectedIndustries.filter { it in industries }.toSet()
        val removedIndustries = originalSelectedIndustries - filteredSelectedIndustries
        selectedIndustries.value = filteredSelectedIndustries

        // Ensure selectedCities only contains valid cities (Set version)
        val originalSelectedCities = selectedCities.value
        val filteredSelectedCities = originalSelectedCities.filter { it in cities }.toSet()
        val removedCities = originalSelectedCities - filteredSelectedCities
        selectedCities.value = filteredSelectedCities

        if (removedContactTypes.isNotEmpty() || removedCities.isNotEmpty() || removedIndustries.isNotEmpty()) {
            log.d("ContactsViewModel") { "Removed invalid filters - filterContacts" }
            filterContacts()
        }
    }
}